import { createAsyncThunk } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import { authApi } from "@/features/auth/authApi";
import {
  authenticatedSessionEstablished,
  clearAuthenticatedSession,
  sessionBooting,
} from "@/features/auth/authSessionSlice";
import type { OnboardingServerStatus } from "@/features/auth/authSessionSchema";
import {
  onboardingApi,
  onboardingProfileToState,
} from "@/features/onboarding/onboardingApi";
import {
  hydrateOnboarding,
  resetOnboarding,
} from "@/features/onboarding/onboardingSlice";
import { authTokenVault } from "@/shared/api/authTokenVault";
import { activeOrganizationVault } from "@/shared/api/activeOrganizationVault";
import { baseApi } from "@/shared/api/baseApi";

interface BootstrapPayload {
  accessToken?: string;
}

export const bootstrapAuthSession = createAsyncThunk<
  OnboardingServerStatus | "guest",
  BootstrapPayload | undefined,
  { state: RootState }
>("authSession/bootstrap", async (payload, { dispatch }) => {
  dispatch(sessionBooting());

  if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_MOCK === "1") {
    const { localMockIdentity } = await import("@/shared/api/localMock");
    const { actor, user } = localMockIdentity;
    activeOrganizationVault.set(actor.activeOrganizationId);
    dispatch(resetOnboarding());
    dispatch(authenticatedSessionEstablished({
      actor,
      onboardingRevision: 1,
      onboardingStatus: "completed",
      user,
    }));
    return "completed";
  }

  try {
    const refreshed = payload?.accessToken
      ? null
      : await dispatch(
          authApi.endpoints.refreshSession.initiate(undefined),
        ).unwrap();
    const accessToken = payload?.accessToken ?? refreshed?.accessToken;
    if (!accessToken) {
      authTokenVault.clear();
      dispatch(clearAuthenticatedSession());
      dispatch(resetOnboarding());
      return "guest";
    }
    authTokenVault.set(accessToken);

    const me = await dispatch(
      authApi.endpoints.getMe.initiate(undefined, {
        forceRefetch: true,
        subscribe: false,
      }),
    ).unwrap();
    activeOrganizationVault.set(me.actor.activeOrganizationId);
    const { profile } = await dispatch(
      onboardingApi.endpoints.getOnboardingProfile.initiate(undefined, {
        forceRefetch: true,
        subscribe: false,
      }),
    ).unwrap();

    if (profile) {
      dispatch(hydrateOnboarding(onboardingProfileToState(profile)));
    } else {
      dispatch(resetOnboarding());
    }

    const onboardingStatus = profile?.status ?? "missing";
    dispatch(
      authenticatedSessionEstablished({
        actor: me.actor,
        onboardingRevision: profile?.revision ?? 0,
        onboardingStatus,
        user: me.user,
      }),
    );
    return onboardingStatus;
  } catch (error) {
    authTokenVault.clear();
    activeOrganizationVault.clear();
    dispatch(clearAuthenticatedSession());
    dispatch(resetOnboarding());
    dispatch(baseApi.util.resetApiState());
    throw error;
  }
});

export const logoutAuthSession = createAsyncThunk<void, void, { state: RootState }>(
  "authSession/logout",
  async (_, { dispatch }) => {
    try {
      await dispatch(authApi.endpoints.logout.initiate()).unwrap();
    } finally {
      authTokenVault.clear();
      activeOrganizationVault.clear();
      dispatch(clearAuthenticatedSession());
      dispatch(resetOnboarding());
      dispatch(baseApi.util.resetApiState());
    }
  },
);
